import { useCallback, useMemo, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useRouter } from 'next/router';

import { AUTH_TOKEN } from 'lib/constants/auth';
import { ROOT, LOGOUT } from 'lib/constants/paths';
import { NEXT_LOCALE, USER_TYPE } from 'lib/constants/cookies';

export type CreateSession = (
  token: string,
  userType: string,
  redirectTo?: string
) => void;
export type EndSession = (redirectTo?: string) => void;

export type UseSessionPayload = {
  createSession: CreateSession;
  endSession: EndSession;
  currentToken: string;
};

/**
 * Hook that exposes functions to handle user sessions.
 */
const useSession = (): UseSessionPayload => {
  const [cookies, setCookie, removeCookie] = useCookies();
  const [currentToken, setCurrentToken] = useState<string>('');
  const router = useRouter();

  const authTokenCookie = cookies[AUTH_TOKEN];

  useEffect(() => {
    setCurrentToken(authTokenCookie);
  }, [authTokenCookie]);

  const createSession = useCallback(
    (token: string, userType: string, redirectTo: string = ROOT) => {
      setCookie(AUTH_TOKEN, token, {
        path: ROOT,
      });

      setCookie(USER_TYPE, userType, {
        path: ROOT,
      });

      router.push(redirectTo);
    },
    [setCookie, router]
  );

  const endSession = useCallback(() => {
    removeCookie(AUTH_TOKEN, {
      path: '/',
    });

    removeCookie(USER_TYPE, {
      path: '/',
    });

    removeCookie(NEXT_LOCALE, {
      path: '/',
    });

    window.location.href = LOGOUT;
  }, [removeCookie]);

  const payload = useMemo<UseSessionPayload>(
    () => ({
      createSession,
      endSession,
      currentToken,
    }),
    [currentToken, createSession, endSession]
  );

  return payload;
};

export default useSession;
